"use client";

import * as React from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import { TrackedLink } from "@/components/analytics/tracked-link";
import { NKEyebrow } from "@/components/nk/eyebrow";
import {
  borrowedBucket,
  captureSimulatorEngaged,
  type SimulatorControl,
} from "@/lib/analytics/events";
import type { UsdCadSeries } from "@/lib/simulator/data/fx-loader";
import type { PrimeRateSeries } from "@/lib/simulator/data/rates-loader";
import type { TickerSnapshot } from "@/lib/simulator/data/ticker-loader";
import { TICKER_LOADERS } from "@/lib/simulator/data/ticker-registry-lazy";
import { runSimulation } from "@/lib/simulator/run";
import {
  estimatedCombinedMarginalRate,
  estimatedDeductibilitySavings,
  JURISDICTIONS,
  type Jurisdiction,
} from "@/lib/simulator/tax";
import { historicalLoan, realStock, type Scenario } from "@/lib/simulator/types";
import { findWorstStretch } from "@/lib/simulator/worst-stretch";
import { windowDataByRange, windowDataForPreset } from "@/lib/simulator/window";
import { aggregateByYear } from "@/lib/simulator/year-aggregator";
import { cn } from "@/lib/utils";

import { BorrowedInput } from "./borrowed-input";
import { BreakdownTable } from "./breakdown-table";
import { DividendHandlingSelect, type DividendHandling } from "./dividend-handling-select";
import { LendingTypeSelect, type LendingType } from "./lending-type-select";
import { SimulatorChart } from "./simulator-chart";
import { TaxSettingsModal } from "./tax-settings-modal";
import { TickerSelect } from "./ticker-select";
import { DEFAULT_WINDOW, WindowPresetSelect, type WindowSelection } from "./window-preset";

/* ──────────────────────────────────────────────────────────────────────────
 * SimulatorInteractive — interactive `/simulate` page (Slice 11 rebuild).
 *
 * Workbench layout, editorial voice:
 *   • Hero — eyebrow + serif headline + two-block subhead, matches /learn.
 *   • Two-column desktop layout — sticky-left inputs in a subtle workbench
 *     panel + scrolling-right results column (pure white). Mobile collapses
 *     to single column with inputs above the chart.
 *   • One emotional center — the outcome headline with the dollar figure as
 *     the only colored text on the page. Sage for positive, rust for negative.
 *   • Worst-stretch supporting line — when the portfolio went underwater,
 *     report the longest stretch's depth + duration. Otherwise "stayed above."
 *   • Expandable year-by-year breakdown table — collapsed by default.
 *   • Tracker-bridge hook block under the outcome + disclaimer at the bottom.
 *   • Three-block disclaimer matching Learn Act 8.
 *
 * Same simulator math, same available options (HELOC only, DRIP only). The
 * lending-type dropdown shows all four options with PLOC / Margin /
 * Investment loan flagged "Coming soon" — forward-compatible layout so
 * Slice 12 can wire them up without restructuring.
 * ────────────────────────────────────────────────────────────────────────── */

// Premium-bank Smith Manoeuvre HELOC spread.
const HELOC_SPREAD = 0.005;

const formatCurrencyAbs = (value: number) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(Math.abs(value));

export interface SimulatorInteractiveProps {
  initialTickerData: TickerSnapshot;
  primeRates: PrimeRateSeries;
  usdCadRates: UsdCadSeries;
}

export function SimulatorInteractive({
  initialTickerData,
  primeRates,
  usdCadRates,
}: SimulatorInteractiveProps) {
  // ── Input state ──────────────────────────────────────────────────────────
  const [ticker, setTicker] = React.useState<string>(initialTickerData.meta.ticker);
  const [borrowed, setBorrowed] = React.useState<number>(100_000);
  const [windowSelection, setWindowSelection] = React.useState<WindowSelection>(DEFAULT_WINDOW);
  const [income, setIncome] = React.useState<number | undefined>(150_000);
  const [jurisdiction, setJurisdiction] = React.useState<Jurisdiction>("BC");
  const [tickerData, setTickerData] = React.useState<TickerSnapshot>(initialTickerData);
  const [isLoadingTicker, setIsLoadingTicker] = React.useState(false);
  const [lendingType, setLendingType] = React.useState<LendingType>("heloc");
  const [dividendHandling, setDividendHandling] = React.useState<DividendHandling>("drip");
  const [showBreakdown, setShowBreakdown] = React.useState(false);

  const handleTickerChange = React.useCallback(
    (next: string) => {
      setTicker(next);
      if (next !== tickerData.meta.ticker) {
        setIsLoadingTicker(true);
      }
    },
    [tickerData.meta.ticker],
  );

  React.useEffect(() => {
    if (tickerData.meta.ticker === ticker) return;
    const loader = TICKER_LOADERS[ticker];
    if (!loader) {
      console.error(`No loader registered for ticker: ${ticker}`);
      return;
    }
    let cancelled = false;
    loader()
      .then((data) => {
        if (cancelled) return;
        setTickerData(data);
        setIsLoadingTicker(false);
      })
      .catch((err) => {
        console.error("Failed to load ticker", ticker, err);
        if (!cancelled) setIsLoadingTicker(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticker, tickerData.meta.ticker]);

  // ── First-interaction tracking ───────────────────────────────────────────
  // Fire `simulator_engaged` once per visit, the first time the user changes
  // any input — the signal that distinguishes "used the simulator" from
  // "viewed the page" (autocapture can't derive it). Snapshot the initial input
  // state on first render, then on each change report which control was touched.
  const engagementSnapshot = React.useRef<{
    ticker: string;
    borrowed: number;
    window: string;
    income: number | undefined;
    jurisdiction: Jurisdiction;
    lendingType: LendingType;
    dividendHandling: DividendHandling;
    showBreakdown: boolean;
  } | null>(null);
  if (engagementSnapshot.current === null) {
    engagementSnapshot.current = {
      ticker,
      borrowed,
      window: JSON.stringify(windowSelection),
      income,
      jurisdiction,
      lendingType,
      dividendHandling,
      showBreakdown,
    };
  }
  const hasEngaged = React.useRef(false);

  React.useEffect(() => {
    if (hasEngaged.current) return;
    const initial = engagementSnapshot.current;
    if (!initial) return;
    let control: SimulatorControl | null = null;
    if (ticker !== initial.ticker) control = "ticker";
    else if (borrowed !== initial.borrowed) control = "borrowed";
    else if (lendingType !== initial.lendingType) control = "lending_type";
    else if (JSON.stringify(windowSelection) !== initial.window) control = "window";
    else if (dividendHandling !== initial.dividendHandling) control = "dividend_handling";
    else if (income !== initial.income) control = "income";
    else if (jurisdiction !== initial.jurisdiction) control = "jurisdiction";
    else if (showBreakdown !== initial.showBreakdown) control = "breakdown";
    if (control) {
      hasEngaged.current = true;
      captureSimulatorEngaged({
        first_interaction: control,
        ticker,
        borrowed_bucket: borrowedBucket(borrowed),
      });
    }
  }, [
    ticker,
    borrowed,
    lendingType,
    windowSelection,
    dividendHandling,
    income,
    jurisdiction,
    showBreakdown,
  ]);

  // ── Derived data (windowed series + FX clamp for USD tickers) ────────────
  const isUsdTicker = tickerData.meta.currency === "USD";
  const fxStartDate = usdCadRates.observations[0]?.date;

  const fxClampedQuotes = React.useMemo(() => {
    if (!isUsdTicker || !fxStartDate) return tickerData.quotes;
    return tickerData.quotes.filter((q) => q.date >= fxStartDate);
  }, [tickerData.quotes, isUsdTicker, fxStartDate]);

  const fxClampedDividends = React.useMemo(() => {
    if (!isUsdTicker || !fxStartDate) return tickerData.dividends;
    return tickerData.dividends.filter((d) => d.date >= fxStartDate);
  }, [tickerData.dividends, isUsdTicker, fxStartDate]);

  // Window the input series — fall back to the full available range if the
  // user's custom-date selection is empty (e.g. after they switched tickers
  // and the saved range no longer overlaps the new ticker's history).
  const applyWindow = React.useCallback(
    <T extends { date: string }>(items: readonly T[]): T[] => {
      if (windowSelection.kind === "preset") {
        return windowDataForPreset(items, windowSelection.preset);
      }
      const sliced = windowDataByRange(items, windowSelection.startDate, windowSelection.endDate);
      return sliced.length > 0 ? sliced : [...items];
    },
    [windowSelection],
  );

  const windowedQuotes = React.useMemo(
    () => applyWindow(fxClampedQuotes),
    [applyWindow, fxClampedQuotes],
  );
  const windowedDividends = React.useMemo(
    () => applyWindow(fxClampedDividends),
    [applyWindow, fxClampedDividends],
  );
  const windowedPrimeObs = React.useMemo(
    () => applyWindow(primeRates.observations),
    [applyWindow, primeRates.observations],
  );

  const scenario = React.useMemo<Scenario>(
    () => ({
      borrowed,
      loanRate: historicalLoan(HELOC_SPREAD, {
        meta: primeRates.meta,
        observations: windowedPrimeObs,
      }),
      stock: realStock(ticker, windowedQuotes, {
        dividends: windowedDividends,
        dividendMode: "drip",
        ...(isUsdTicker ? { fxSeries: usdCadRates } : {}),
      }),
      capitalizeInterest: true,
    }),
    [
      borrowed,
      ticker,
      windowedQuotes,
      windowedDividends,
      windowedPrimeObs,
      primeRates.meta,
      isUsdTicker,
      usdCadRates,
    ],
  );

  const result = React.useMemo(() => runSimulation(scenario), [scenario]);

  // ── Net wealth + outcome ─────────────────────────────────────────────────
  // Per /learn Act 2: net = (Portfolio - Loan) + cash_dividends_outside + tax_refunds.
  // For DRIP (v1 default), the dividend cash is reinvested into portfolio, so
  // the outside term is 0 — net collapses to (Portfolio - Loan) + tax_refunds.
  const interestPaid = result.final.loanBalance - scenario.borrowed;
  const marginalRate = React.useMemo(
    () => (income !== undefined ? estimatedCombinedMarginalRate(income, jurisdiction) : 0),
    [income, jurisdiction],
  );
  const cumulativeTaxRefunds = estimatedDeductibilitySavings(interestPaid, marginalRate);
  const netWealth = result.final.portfolioValue - result.final.loanBalance + cumulativeTaxRefunds;
  const isPositive = netWealth >= 0;

  // ── Worst stretch ────────────────────────────────────────────────────────
  const worstStretch = React.useMemo(() => findWorstStretch(result.series), [result]);

  // ── Year-by-year breakdown ───────────────────────────────────────────────
  const yearSummaries = React.useMemo(
    () => aggregateByYear(result.series, marginalRate, "drip"),
    [result.series, marginalRate],
  );

  // ── UI surfacing ─────────────────────────────────────────────────────────
  const hasIncome = income !== undefined && income > 0;
  const jurisdictionMeta = JURISDICTIONS[jurisdiction];
  const showJurisdictionFallbackNotice = hasIncome && !jurisdictionMeta.supported;

  return (
    <main className="bg-stone-50">
      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 pt-16 pb-10 md:pt-24 md:pb-14">
        <div className="mx-auto max-w-3xl">
          <NKEyebrow>The Simulator</NKEyebrow>
          <h1 className="font-display mt-6 text-4xl font-medium tracking-tight text-zinc-900 md:text-5xl">
            See what your version would have done.
          </h1>
          <div className="mt-6 space-y-2 text-base leading-relaxed text-zinc-700 md:text-lg">
            <p>Pick your stock, your loan, your time period.</p>
            <p>We&rsquo;ll run it against real history.</p>
          </div>
        </div>
      </section>

      {/* ── Two-column dashboard layout ─────────────────────────────────────── */}
      <div className="relative overflow-x-clip pb-24">
        {/* Full-bleed left-column background — extends to viewport edge so the
            workbench reads as a region of the page, not a widget. Hidden below
            the lg breakpoint where the layout collapses to a single column. */}
        <div
          aria-hidden
          className="absolute top-0 bottom-0 left-0 hidden bg-stone-100/70 lg:block"
          style={{ width: "calc(max((100vw - 1280px) / 2, 0px) + 1.5rem + 280px + 1rem)" }}
        />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[280px_1fr] lg:items-start lg:gap-10 xl:gap-14">
          {/* Left column — inputs (workbench: typography + whitespace only,
              no card borders, single hairline between sections) */}
          <aside>
            <div className="bg-stone-100/70 p-5 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:bg-transparent lg:p-0 lg:py-8 lg:pr-2">
              <InputSection title="Your borrowing">
                <InputField label="Amount borrowed">
                  <BorrowedInput value={borrowed} onChange={setBorrowed} />
                </InputField>
                <InputField label="Lending type">
                  <LendingTypeSelect value={lendingType} onChange={setLendingType} />
                </InputField>
                <InputField label="Interest rate">
                  <DisplayValue>Prime + {(HELOC_SPREAD * 100).toFixed(2)}%</DisplayValue>
                </InputField>
              </InputSection>

              <hr className="my-6 border-zinc-200" />

              <InputSection title="Your investment">
                <InputField label="Stock">
                  <TickerSelect
                    value={ticker}
                    onChange={handleTickerChange}
                    disabled={isLoadingTicker}
                  />
                </InputField>
                <InputField label="Dividend handling">
                  <DividendHandlingSelect value={dividendHandling} onChange={setDividendHandling} />
                </InputField>
                <InputField label="When did you start investing?">
                  <WindowPresetSelect
                    value={windowSelection}
                    onChange={setWindowSelection}
                    minDate={fxClampedQuotes[0]?.date ?? ""}
                    maxDate={fxClampedQuotes[fxClampedQuotes.length - 1]?.date ?? ""}
                    disabled={isLoadingTicker}
                  />
                </InputField>
              </InputSection>

              <hr className="my-6 border-zinc-200" />

              <InputSection title="Settings">
                <TaxSettingsModal
                  income={income}
                  onIncomeChange={setIncome}
                  jurisdiction={jurisdiction}
                  onJurisdictionChange={setJurisdiction}
                  jurisdictionLabel={jurisdictionMeta.name}
                />
                {showJurisdictionFallbackNotice ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
                    Province bracket data coming soon — using British Columbia rates as an estimate
                    for now.
                  </p>
                ) : null}
              </InputSection>
            </div>
          </aside>

          {/* Right column — dashboard (no max-width, fills available space) */}
          <div className="lg:pl-2 xl:pl-4">
            {/* ── The Outcome (top of dashboard — the narrative answer) ──── */}
            <section className="lg:pt-6">
              <NKEyebrow>The Outcome</NKEyebrow>
              <h2 className="font-display mt-5 text-3xl font-medium tracking-tight text-zinc-900 md:text-4xl">
                {isPositive ? (
                  <>
                    You ended with{" "}
                    <span
                      className="font-medium tabular-nums"
                      style={{ color: "var(--color-nk-outcome-positive)" }}
                    >
                      {formatCurrencyAbs(netWealth)}
                    </span>{" "}
                    more wealth than you started.
                  </>
                ) : (
                  <>
                    You ended{" "}
                    <span
                      className="font-medium tabular-nums"
                      style={{ color: "var(--color-nk-outcome-negative)" }}
                    >
                      {formatCurrencyAbs(netWealth)}
                    </span>{" "}
                    underwater compared to where you started.
                  </>
                )}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-zinc-700 md:text-lg">
                {worstStretch ? (
                  <>
                    Worst stretch —{" "}
                    <span className="tabular-nums">
                      {formatCurrencyAbs(worstStretch.depthDollars)}
                    </span>{" "}
                    underwater for {worstStretch.durationMonths}{" "}
                    {worstStretch.durationMonths === 1 ? "month" : "months"} in{" "}
                    {worstStretch.yearOfDeepest}.
                  </>
                ) : (
                  "Your portfolio stayed above your loan balance throughout."
                )}
              </p>
            </section>

            {/* ── Chart (large, dominates the right column) ──────────────── */}
            <div
              className={cn(
                "mt-10 transition-opacity",
                isLoadingTicker && "animate-pulse opacity-60",
              )}
              aria-busy={isLoadingTicker}
            >
              <SimulatorChart result={result} />
            </div>

            {/* ── Legend (color swatches + labels under the chart) ───────── */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-zinc-600 sm:justify-start">
              <span className="inline-flex items-center gap-2">
                <span
                  aria-hidden
                  className="inline-block h-[2px] w-6"
                  style={{ background: "var(--color-nk-brand)" }}
                />
                Portfolio value
              </span>
              <span className="inline-flex items-center gap-2">
                <span
                  aria-hidden
                  className="inline-block h-[2px] w-6 border-t-2 border-dashed"
                  style={{ borderColor: "var(--color-nk-accent)" }}
                />
                Loan balance
              </span>
              <span className="inline-flex items-center gap-2">
                <span
                  aria-hidden
                  className="inline-block h-3 w-6"
                  style={{ background: "var(--color-nk-chart-wealth-fill)" }}
                />
                Wealth
              </span>
              {worstStretch ? (
                <span className="inline-flex items-center gap-2">
                  <span
                    aria-hidden
                    className="inline-block h-3 w-6"
                    style={{ background: "var(--color-nk-chart-drawdown-fill)" }}
                  />
                  Drawdown
                </span>
              ) : null}
            </div>

            {/* ── Tracker hook (sits between outcome and breakdown — natural
                decision point right after the user gets their answer) ────── */}
            <section className="mt-12 md:mt-14">
              <h3 className="font-display text-2xl font-medium tracking-tight text-zinc-900 md:text-3xl">
                Ready to try the real thing?
              </h3>
              <p className="mt-4 text-base leading-relaxed text-zinc-700 md:text-lg">
                We&rsquo;re building a tool to track your real portfolio — or let you practice with
                a simulated one first.
              </p>
              <div className="mt-5">
                <TrackedLink
                  href="/tracker"
                  event="tracker_cta_clicked"
                  source="simulator_hook"
                  className="font-display inline-flex items-center gap-2 text-base font-medium tracking-tight text-zinc-900 underline-offset-4 hover:underline md:text-lg"
                >
                  Read more about the Tracker
                  <ArrowRight className="size-4" aria-hidden />
                </TrackedLink>
              </div>
            </section>

            {/* ── Breakdown affordance ───────────────────────────────────── */}
            <div className="mt-12">
              <button
                type="button"
                onClick={() => setShowBreakdown((v) => !v)}
                aria-expanded={showBreakdown}
                className="font-display text-base font-medium tracking-tight text-zinc-900 underline-offset-4 hover:underline"
              >
                {showBreakdown ? "Hide breakdown ↑" : "Show me the breakdown ↓"}
              </button>

              {showBreakdown ? (
                <BreakdownTable years={yearSummaries} showTaxRefund={hasIncome} />
              ) : null}
            </div>

            {/* ── Disclaimer (one-line link to /disclaimer) ──────────────── */}
            <p className="mt-20 text-sm text-zinc-500 md:mt-24">
              Not investment or tax advice.{" "}
              <Link href="/disclaimer" className="underline underline-offset-4 hover:text-zinc-700">
                See full disclaimer →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

// ── Layout helpers (workbench style — no card containers) ────────────────────

function InputSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <p className="font-mono text-[11px] font-medium tracking-[0.22em] text-zinc-700 uppercase">
        {title}
      </p>
      <dl className="mt-4 grid grid-cols-1 gap-y-4">{children}</dl>
    </section>
  );
}

function InputField({
  label,
  helperText,
  children,
}: {
  label: string;
  helperText?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[10px] font-medium tracking-[0.18em] text-zinc-500 uppercase">
        {label}
      </dt>
      <dd className="mt-1">{children}</dd>
      {helperText ? (
        <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">{helperText}</p>
      ) : null}
    </div>
  );
}

function DisplayValue({ children }: { children: React.ReactNode }) {
  return <p className="text-sm font-medium text-zinc-900 tabular-nums">{children}</p>;
}
